"use client";

import { useLayoutEffect } from "react";
import { looksLikeLineEmbeddedBrowser } from "@/lib/openLineExternalBrowser";
import { useSigninSheetsReconnectOAuthReturn } from "@/hooks/useSigninSheetsReconnectOAuthReturn";
import {
  clearFinancialWorkIntent,
  readFinancialWorkIntent,
} from "@/lib/financialWork/intent";

/**
 * LINE LIFF can reopen the mini app with `?path=/target?...` (see openLineExternalBrowser.ts).
 * Handle it globally so deep-links work from any entry page (not only /signin).
 *
 * Also recovers Financial Work opaque intent when the user lands on `/` after
 * session expiry without a callbackUrl (shell restore covers /service return).
 */
export default function LiffPathRedirect() {
  useSigninSheetsReconnectOAuthReturn(looksLikeLineEmbeddedBrowser());

  useLayoutEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("path");
    if (!raw) return;

    const key = `liff_path_redirected:${raw}`;
    try {
      if (sessionStorage.getItem(key) === "1") return;
      sessionStorage.setItem(key, "1");
    } catch {
      // ignore
    }

    let decoded = raw;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      decoded = raw;
    }

    if (decoded.startsWith("/") && !decoded.startsWith("//")) {
      window.location.replace(decoded);
    }
  }, []);

  useLayoutEffect(() => {
    const path = window.location.pathname;
    const normalized = path.length > 1 ? path.replace(/\/+$/, "") : path;
    // Only hijack true home — never steal /service or signin flows.
    if (normalized !== "/") return;
    if (new URLSearchParams(window.location.search).get("path")) return;

    const intent = readFinancialWorkIntent();
    if (!intent?.returnPath?.startsWith("/service/")) return;

    const key = `fw_intent_home_redirect:${intent.returnPath}`;
    try {
      if (sessionStorage.getItem(key) === "1") {
        clearFinancialWorkIntent();
        return;
      }
      sessionStorage.setItem(key, "1");
    } catch {
      // ignore
    }

    clearFinancialWorkIntent();
    window.location.replace(intent.returnPath);
  }, []);

  return null;
}
